/**
 * Company API
 *
 * API endpoints for company management operations
 */

import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import type { Companies, Company, CreateCompanyPayload, UpdateCompanyPayload } from "./company.types";
import { normalizeCompanyPayload } from "./company.utils";

/**
 * Company API endpoints
 * Provides CRUD operations for company management
 */
export const companyApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get a single company by Host
     * @param host - Company host
     * @returns Full company details
     */
    getCompanyByHost: build.query<{ id: number; logo: string; name: string }, { host: string }>({
      query: (params) => ({
        url: "/company/host/",
        params
      })
    }),

    /**
     * Get all companies
     * @returns Array of companies with basic information
     */
    getCompanies: build.query<Companies[], void>({
      query: () => "/company",
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando empresas...",
          success: "Empresas carregadas com sucesso!",
          error: "Erro ao carregar empresas."
        });
      }
    }),

    /**
     * Get a single company by ID
     * @param id - Company ID
     * @returns Full company details including contacts
     */
    getCompany: build.query<Company, Pick<Company, "id">>({
      query: ({ id }) => `/company/${id}`,
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando...",
          success: "Empresa carregada com sucesso!",
          error: "Erro ao carregar empresa."
        });
      }
    }),

    /**
     * Create a new company
     * @param payload - Company data with id set to null
     * @returns void
     */
    createCompany: build.mutation<void, CreateCompanyPayload>({
      query: ({ id = null, ...payload }) => ({
        url: "/company/create",
        method: "POST",
        body: {
          id,
          ...normalizeCompanyPayload(payload)
        }
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Cadastrando...",
          success: "Empresa cadastrada com sucesso!",
          error: "Erro ao cadastrar empresa."
        });
      }
    }),

    /**
     * Update an existing company
     * @param payload - Company data with id
     * @returns void
     */
    updateCompany: build.mutation<void, UpdateCompanyPayload>({
      query: ({ id, ...payload }) => ({
        url: "/company/create",
        method: "POST",
        body: {
          id,
          ...normalizeCompanyPayload(payload)
        }
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Atualizando...",
          success: "Empresa atualizada com sucesso!",
          error: "Erro ao atualizar empresa."
        });
      }
    }),

    /**
     * Delete a company
     * @param id - Company ID to delete
     * @returns void
     */
    deleteCompany: build.mutation<void, Pick<Companies, "id">>({
      query: ({ id }) => ({
        url: `/company/delete/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Excluindo...",
          success: "Empresa excluída com sucesso!",
          error: "Erro ao excluir empresa."
        });
      }
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
companyApi.enhanceEndpoints({
  addTagTypes: ["Companies", "Company", "CompanyByHost"],
  endpoints: {
    getCompanyByHost: {
      providesTags: (result, err, { host }) => [{ type: "CompanyByHost", id: result?.id ?? host }]
    },
    getCompanies: {
      providesTags: (result) =>
        result ? [...result.map(({ id }) => ({ type: "Companies" as const, id })), { type: "Companies", id: "LIST" }] : [{ type: "Companies", id: "LIST" }]
    },
    getCompany: {
      providesTags: (result, error, { id }) => [{ type: "Company", id }]
    },
    createCompany: {
      invalidatesTags: (response, error) => (!error ? [{ type: "Companies", id: "LIST" }] : [])
    },
    updateCompany: {
      invalidatesTags: (response, error, { id }) =>
        !error
          ? [
              { type: "Companies", id: "LIST" },
              { type: "Company", id }
            ]
          : []
    },
    deleteCompany: {
      invalidatesTags: (response, error, { id }) =>
        !error
          ? [
              { type: "Companies", id: "LIST" },
              { type: "Company", id }
            ]
          : []
    }
  }
});
